import { useMessage } from "@/common/messages";
import { useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import {
	AttachmentData,
	EmailData,
	UploadStatus,
} from "./types";
import {
	deleteDraftAttachmentFn,
	uploadDraftAttachmentFn,
} from "@/repository/queryFns";
import { base64ToFile, fileToBase64 } from "@/common/helpers";
import { SendAttachmentData } from "@/repository/models/message";
import { captureError } from "@/common/helpers/sentryHelper";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { wouldExceedAttachmentLimit } from "@/common/helpers/emailHelper";
import { useEmailAttachmentLimits } from "./useEmailAttachmentLimits";
import axios, { HttpStatusCode } from "axios";

export const useEmailAttachments = (
	data: EmailData | undefined,
	onChange: (
		data: EmailData,
		isMainContentChanged: boolean,
		draftNeedsUpdate: boolean,
		callingMethod: string,
		attachmentsAdded?: boolean
	) => void,
	sessionId?: string
) => {
	const intl = useIntl();
	const { show: showMessage } = useMessage();

	const { totalMB: emailMaxTotalAttachmentSizeMB, perFileMB: emailMaxAttachmentSizeMB, totalBytes: emailMaxTotalAttachmentSizeBytes, perFileBytes: emailMaxAttachmentSizeBytes } = useEmailAttachmentLimits();

	const dataRef = useRef(data);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	const validateFiles = useCallback(
		(files: File[]): boolean => {
			const currentData = dataRef.current;

			if (!currentData) {
				return false;
			}

			if (files.length > 5) {
				showMessage("workspace", {
					intent: "error",
					text: intl.formatMessage(
						{ id: "Interaction.EmailEditor.MaxNumberOfFiles" },
						{ numberOfFiles: 5 }
					),
				});
				return false;
			}

			const duplicateNames = files
				.filter((x) =>
					currentData.attachments.some((f) => f.file.name == x.name)
				)
				.map((x) => x.name)
				.join(", ");

			if (duplicateNames.length > 0) {
				showMessage("workspace", {
					intent: "error",
					text: intl.formatMessage(
						{ id: "Interaction.EmailEditor.FilesAlreadyAttached" },
						{ filenames: duplicateNames }
					),
				});
				return false;
			}

			const filesWithMaxSizeExceeded = files
				.filter((x) => x.size > emailMaxAttachmentSizeBytes)
				.map((x) => x.name)
				.join(", ");

			if (filesWithMaxSizeExceeded) {
				showMessage("workspace", {
					intent: "error",
					text: intl.formatMessage(
						{ id: "Interaction.EmailEditor.MaxSizeExceeded" },
						{
							size: emailMaxAttachmentSizeMB,
							filenames: filesWithMaxSizeExceeded,
						}
					),
				});
				return false;
			}

			var sizeOfAddedAttachments = files.reduce(
				(acc, att) => acc + att.size,
				0
			);

			if (
				wouldExceedAttachmentLimit(
					currentData.attachments,
					sizeOfAddedAttachments,
					emailMaxTotalAttachmentSizeBytes
				)
			) {
				showMessage("workspace", {
					text: intl.formatMessage(
						{
							id: "Interaction.EmailEditor.TotalAttachmentsSizeWouldBeExceeded",
						},
						{ maxSize: emailMaxTotalAttachmentSizeMB }
					),
					intent: "error",
				});

				return false;
			}

			return true;
		},
		[onChange]
	);

	const uploadDraftAttachment = useCallback(
		async (attachmentSendData: SendAttachmentData) => {
			const currentData = dataRef.current;

			if (!currentData) {
				return;
			}

			if (!sessionId) {
				return;
			}

			const messageId = currentData.refMessage?.id;

			if (!messageId) {
				return;
			}

			if (!attachmentSendData.contentBytes && !attachmentSendData.toRemove) {
				return;
			}

			const attachmentTempId = attachmentSendData.id;
			uploadDraftAttachmentFn(sessionId, messageId, attachmentSendData)
				.then((message) => {
					const currentDataUpdated = dataRef.current;

					if (!currentDataUpdated) {
						return;
					}

					onChange(
						{
							...currentDataUpdated,
							attachments: currentDataUpdated.attachments.map((att) => {
								const attachmentInProgressFound =
									att.id === attachmentTempId ||
									(att.contentId &&
										att.contentId == attachmentSendData.contentId);

								return attachmentInProgressFound
									? {
										...att,
										id: message,
										uploadStatus: UploadStatus.Completed,
									}
									: att;
							}),
						},
						false,
						false,
						"uploadDraftAttachmentFn",
						true
					);
				})
				.catch((error: any) => {
					captureError(error, "Error uploading draft attachment", {
						interactionId: sessionId,
						draftMessageId: currentData.refMessage?.id,
					});

					if (currentInteraction?.id !== sessionId) {
						return;
					}

					const statusCode = axios.isAxiosError(error) ? error.response?.status : undefined;

					if (statusCode === HttpStatusCode.PayloadTooLarge) {
						const inlineSuffix = attachmentSendData.isInline
							? ` ${intl.formatMessage({ id: "Interaction.EmailEditor.ErrorExchangeLimitExceededInline" })}`
							: "";
						showMessage("workspace", {
							intent: "error",
							bodyStyle: {
					            textWrap: "wrap",
					            paddingTop: "8px",
					            paddingBottom: "8px",
				            },
							text: `${intl.formatMessage({ id: "Interaction.EmailEditor.ErrorExchangeLimitExceededUpload" })} ${intl.formatMessage({ id: "Interaction.EmailEditor.ErrorExchangeLimitExceededBody" })}${inlineSuffix}`,
						});
					}
					else if (attachmentSendData.isInline) {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage({
								id: "Interaction.EmailEditor.ErrorUploadingInlineAttachment",
							}),
						});
					} else {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage(
								{ id: "Interaction.EmailEditor.ErrorUploadingFile" },
								{ filename: attachmentSendData.name }
							),
						});
					}

					const currentDataUpdated = dataRef.current;

					if (!currentDataUpdated) {
						return;
					}

					onChange(
						{
							...currentDataUpdated,
							attachments: currentDataUpdated.attachments.filter((att) => {
								return att.contentId !== attachmentSendData.contentId && att.id != attachmentTempId;
							}),
						},
						false,
						false,
						"uploadDraftAttachmentFn",
						true
					);
				});
		},
		[uploadDraftAttachmentFn, onChange]
	);

	const validateAndUploadDraftAttachments = useCallback(
		(files: File[]) => {
			const currentData = dataRef.current;

			if (!currentData) {
				return;
			}

			var filesAreValid = validateFiles(files);

			if (!filesAreValid) {
				return;
			}

			const fileAttachments = files.map(
				(file) =>
					({
						file: file,
						id: crypto.randomUUID(),
						uploadStatus: UploadStatus.InProgress,
					}) as AttachmentData
			);

			fileAttachments.forEach(async (attachment) => {
				try {
					const attachmentSendData = {
						id: attachment.id,
						name: attachment.file.name,
						contentType: attachment.file.type,
						contentId: attachment.contentId,
						isInline: !!attachment.contentId,
						contentBytes: await fileToBase64(attachment.file),
						toRemove: false,
					};

					uploadDraftAttachment(attachmentSendData);
				} catch (error: any) {
					captureError(error, "Error uploading draft attachment", {
						interactionId: sessionId,
						draftMessageId: currentData.refMessage?.id,
					});
					console.error(error);
				}
			});

			onChange(
				{
					...currentData,
					attachments: [...currentData.attachments, ...fileAttachments],
				},
				false,
				false,
				"validateAndUploadDraftAttachments",
				true
			);
		},
		[sessionId, onChange, uploadDraftAttachmentFn, validateFiles]
	);

	const uploadDraftInlineAttachments = useCallback(
		(attachments: SendAttachmentData[]) => {
			const currentData = dataRef.current;

			if (!currentData) {
				return;
			}

			const attachmentDatas = attachments.map(
				(att) =>
					({
						file: base64ToFile(att.contentBytes, att.contentType, att.name),
						contentId: att.contentId,
						uploadStatus: UploadStatus.InProgress,
					}) as AttachmentData
			);
			onChange(
				{
					...currentData,
					attachments: [...currentData.attachments, ...attachmentDatas],
				},
				false,
				false,
				"uploadDraftInlineAttachments",
				true
			);

			setTimeout(() => {
				attachments.forEach((attachment) => {
					uploadDraftAttachment(attachment);
				});
			}, 0);
		},
		[uploadDraftAttachmentFn, onChange]
	);

	const deleteDraftAttachment = useCallback(
		(attachment: AttachmentData) => {
			if (!attachment.id || !sessionId) {
				return;
			}

			const messageId = dataRef.current?.refMessage?.id;

			if (!messageId) {
				return;
			}

			deleteDraftAttachmentFn(sessionId, messageId, attachment.id)
				.then(() => {
					removeModelAttachment(attachment);
				})
				.catch((error: any) => {
					captureError(error, "Error deleting draft attachment", {
						interactionId: sessionId,
						draftMessageId: messageId,
					});

					console.error(error);

					if (currentInteraction?.id !== sessionId) {
						return;
					}

					if (attachment.contentId) {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage({
								id: "Interaction.EmailEditor.ErrorDeletingInlineAttachment",
							}),
						});
					} else {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage(
								{ id: "Interaction.EmailEditor.ErrorDeletingAttachment" },
								{ filename: attachment.file.name }
							),
						});
					}
				});
		},
		[deleteDraftAttachmentFn, sessionId, onChange]
	);

	const validateAndAddModelAttachments = useCallback(
		(files: File[]) => {
			const currentData = dataRef.current;

			if (!currentData) {
				return;
			}

			var filesAreValid = validateFiles(files);

			if (!filesAreValid) {
				return;
			}

			const fileAttachments = files.map(
				(file) =>
					({
						file: file,
						uploadStatus: UploadStatus.None,
					}) as AttachmentData
			);

			onChange(
				{
					...currentData,
					attachments: [...currentData.attachments, ...fileAttachments],
				},
				false,
				false,
				"validateAndAddModelAttachments",
				true
			);
		},
		[onChange, validateFiles]
	);

	const removeModelAttachment = useCallback(
		(attachment: AttachmentData) => {
			const currentData = dataRef.current;

			if (!currentData) {
				return;
			}

			onChange(
				{
					...currentData,
					attachments: currentData.attachments.filter(
						(x) => x.id !== attachment.id
					),
				},
				false,
				false,
				"removeModelAttachment",
				true
			);
		},
		[onChange]
	);

	return {
		validateAndUploadDraftAttachments,
		deleteDraftAttachment,
		validateAndAddModelAttachments,
		removeModelAttachment,
		uploadDraftInlineAttachments,
	};
};
